/* eslint-disable max-lines */
import { useCallback, useRef, useState, useEffect, SetStateAction, Dispatch } from 'react';

import { Delete } from '@mui/icons-material';
import { IconButton, useTheme } from '@mui/material';
import { TableColumn } from 'components/Table';
import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import {
  FieldValues,
  Path,
  useController,
  UseControllerProps,
  UseFormGetValues,
  UseFormReset,
  UseFormSetValue,
} from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { PromptDialog } from 'components/Dialogs';
import strings, { url } from 'constants/index';
import { useSports } from 'contexts/SportContext';
import { CompetitorsTeamDetailsFormValues, ContractItem } from 'features/competitors';
import { urlByCompetitorTypeMapping } from 'features/competitors/Competitors.utils';
import { CompetitorPlayer } from 'features/competitors/players/Players.types';
import { useDialogStatus, useTableFilter } from 'hooks/common';
import { useCompetitorsType } from 'hooks/context';
import { buildRequestParams } from 'utils/pagination.utils';

import { BaseCompetitorsFormField } from './BaseCompetitorsFormField';
import { getCompetitorStateColors } from './CompetitorsFormField.styles';

interface CompetitorsFormFieldProps {
  sportId?: number;
  required?: boolean;
  customColumns?: Array<TableColumn>;
  customFilters?: Array<PaginationFilter>;
  isActiveContracts: boolean;
  setActiveContracts: Dispatch<SetStateAction<boolean>>;
  contractsMetadata: EntityMetaProperty[];
  refetchCompetitors: () => Promise<void>;
  teamMetadata: MetaProperty[];
  setValue: UseFormSetValue<CompetitorsTeamDetailsFormValues>;
  getValues: UseFormGetValues<CompetitorsTeamDetailsFormValues>;
  resetForm: UseFormReset<CompetitorsTeamDetailsFormValues>;
  genderType: string;
}
interface WithCompetitorsValues extends FieldValues {
  competitors: Array<EditableCompetitor>;
}

export function CompetitorsFormFieldSquad<T extends WithCompetitorsValues>({
  control,
  required,
  sportId,
  customColumns,
  customFilters,
  isActiveContracts,
  setActiveContracts,
  contractsMetadata,
  refetchCompetitors,
  teamMetadata,
  setValue,
  getValues,
  resetForm,
  genderType,
}: UseControllerProps<T> & CompetitorsFormFieldProps) {
  const [rows, setRows] = useState<EditableCompetitor[]>([]);
  const { id } = useParams<string>();
  const scrollRef = useRef(null);
  const idsRef = useRef({});
  const [competitorsType] = useCompetitorsType();
  const { sports } = useSports();
  const [pendingCompetitor, setPendingCompetitor] = useState<EditableCompetitor | null>(null);
  const dialogResolverRef = useRef<((confirmed: boolean) => void) | null>(null);
  const {
    isOpen: isContractConfirmationOpen,
    onShow: onShowContractConfirmation,
    onHide: onHideContractConfirmation,
  } = useDialogStatus();
  const {
    field: { onChange, value: competitors },
  } = useController<T>({
    name: 'competitors' as Path<T>,
    control,
    rules: {
      required: { value: required, message: strings.field_is_required },
    },
  });
  const theme = useTheme();
  const stateColors = getCompetitorStateColors(theme);

  const getOldContractTeamMetadata = async (teamSportId: number) => {
    if (!teamSportId) {
      return false;
    }
    const result = await axios.get(`/properties/entityTypes/Competitor/sport/${teamSportId}`);
    return result.data.find(m => m.name === 'IsNationalTeam')?.value === 'yes';
  };

  const handleDelete = useCallback(
    async (competitor: EditableCompetitor) => {
      if (competitor.added) {
        // Item is newly added - remove it, we do not need it
        onChange(competitors.filter((item: EditableCompetitor) => item.id !== competitor.id));
      } else {
        await axios.patch('/contracts', {
          id: competitor.contractId,
          isActive: false,
        });
        await refetchCompetitors();
      }
    },
    [competitors, onChange, refetchCompetitors],
  );

  const handleEditCell = useCallback(
    (columnId: string | number, value: any, competitor: Record<string, any>) => {
      const clonnedItem = { ...competitor };
      const loweredColumnId = columnId.toString().toLowerCase();

      if (loweredColumnId === 'shirtnumber' && /^\d{0,4}$/.test(value)) {
        clonnedItem[columnId] = value || '0';
      } else if (
        (loweredColumnId === 'position' &&
          contractsMetadata.some(
            cm => cm.name === 'Position' && cm.values.some(v => v === value),
          )) ||
        loweredColumnId === 'isactive'
      ) {
        clonnedItem[columnId] = value;
      } else if (loweredColumnId === 'startdate' || loweredColumnId === 'enddate') {
        clonnedItem[columnId] = value.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
      } else {
        throw new Error('some message');
      }

      if (competitor.added) {
        return clonnedItem;
      }
      const initialRow = competitors.find(i => i.id === competitor.id);
      if (initialRow?.[columnId] !== value) {
        clonnedItem.edited = true;
      } else {
        delete clonnedItem.edited;
      }

      return clonnedItem;
    },
    [competitors, contractsMetadata],
  );

  const populateMetadata = (
    competitor: EditableCompetitor,
    contractsMetadata: EntityMetaProperty[],
  ) => {
    const metaData = contractsMetadata
      .map(({ name }) => ({
        name,
        value: competitor[name] || '',
      }))
      .filter(x => x.value !== '');

    return metaData;
  };

  const { setSortBy, setFilterBy, filteredItems } = useTableFilter<EditableCompetitor>(
    competitors || [],
    { columns: customColumns },
  );

  const handleEdit = async (columnId: string | number, value: any, row: Record<string, any>) => {
    const competitor = cloneDeep(row);
    delete competitor.actions;

    const updatedCompetitors = competitors.map((item: EditableCompetitor) => {
      if (item.id === competitor.id) {
        const editedCompetitor = handleEditCell(columnId, value, competitor);
        return editedCompetitor;
      }
      return item;
    });

    setValue('competitors', updatedCompetitors, { shouldDirty: false });

    const oldValues = getValues();
    resetForm({
      ...oldValues,
      competitors: updatedCompetitors,
    });
  };

  const handleBackgroundColorChange = useCallback(
    (competitorId: number, backgroundColor: string) => {
      const parentElement = idsRef.current[competitorId]?.parentElement;

      parentElement.parentElement.style.backgroundColor = backgroundColor;
      parentElement.style.backgroundColor = backgroundColor;
    },
    [],
  );

  const handleAdd = useCallback(
    async competitor => {
      let contract: ContractItem;
      let player: CompetitorPlayer;
      if (competitorsType === strings.person) {
        try {
          const { data: contracts } = await axios.get<PaginationData<ContractItem>>(
            `${urlByCompetitorTypeMapping[competitorsType]}/${competitor.id}${url.contracts}`,
            {
              params: buildRequestParams({
                filters: [
                  { columnId: 'isActive', value: 'false' },
                  { columnId: 'competitor.id', value: id },
                ],
              }),
            },
          );
          contract = contracts.items[0];
          if (!contract) {
            const { data: players } = await axios.get<PaginationData<CompetitorPlayer>>(
              `${url.players}`,
              {
                params: buildRequestParams({
                  filters: [{ columnId: 'personId', value: competitor.id }],
                }),
              },
            );
            player = players.items[0];
          }
        } catch {
          // ignore
        }
      }

      const clonnedCompetitors: Array<any> = competitors ? cloneDeep(competitors) : [];
      clonnedCompetitors.push({
        contractId: contract?.id || null,
        id: competitor.id,
        name: competitorsType === strings.person ? competitor.fullName : competitor.name,
        ShirtNumber: '0',
        ...competitor,
        ...contract?.metadataProperties?.reduce(
          (result, { name, value }) => ({
            ...result,
            [name]: value || '',
          }),
          {},
        ),
        ...player?.metadataProperties?.reduce(
          (result, { name, value }) => ({
            ...result,
            [name]: value || '',
          }),
          {},
        ),
        ...competitor.metadataProperties?.reduce(
          (result, { name, value }) => ({
            ...result,
            [name]: value || '',
          }),
          {},
        ),
        added: true,
      });
      const sortedCompetitors = clonnedCompetitors.sort((firstCompetitor, secondCompetitor) =>
        firstCompetitor.id < secondCompetitor.id ? -1 : 1,
      );
      onChange(sortedCompetitors);
      // Fire the scroll action after the render
      setTimeout(
        () =>
          scrollRef.current?.scrollBy({
            top: scrollRef.current?.scrollHeight,
            behavior: 'smooth',
          }),
        0,
      );
    },
    [competitors, competitorsType, onChange, id],
  );

  useEffect(() => {
    setRows(
      filteredItems.map(competitor => ({
        ...competitor,
        actions: {
          value: (
            <IconButton
              data-testid="add-relation-delete"
              title="delete"
              onClick={() => handleDelete(competitor)}
              size="large"
            >
              <Delete />
            </IconButton>
          ),
        },
      })),
    );
  }, [filteredItems, handleDelete]);

  const askContractConfirmation = useCallback(
    (competitor: EditableCompetitor) =>
      new Promise<boolean>(resolve => {
        dialogResolverRef.current = resolve;
        setPendingCompetitor(competitor);
        onShowContractConfirmation();
      }),
    [onShowContractConfirmation],
  );

  const handleContractConfirmation = useCallback(
    (isConfirmed: boolean) => {
      const resolver = dialogResolverRef.current;
      dialogResolverRef.current = null;
      setPendingCompetitor(null);
      onHideContractConfirmation();
      if (resolver) {
        resolver(isConfirmed);
      }
    },
    [onHideContractConfirmation],
  );

  const handleSaveAll = useCallback(async () => {
    const modifiedCompetitors = competitors.filter(
      (competitor: EditableCompetitor) => competitor.edited || competitor.added,
    );
    if (modifiedCompetitors.length === 0) {
      return;
    }

    const isCurrentTeamNationalTeam =
      teamMetadata.find(m => m.name === 'IsNationalTeam')?.value === 'yes';
    const isCricket = sports?.find(x => x.id === sportId)?.name === 'Cricket';

    const savedById = new Map<number, { contractId?: number }>();

    for (const competitor of modifiedCompetitors) {
      try {
        // For newly added competitors, check whether saving would overwrite an
        // existing active contract on a non-national team (and current team is
        // not national, sport is not cricket). If so, prompt the user before
        // proceeding. On cancel we skip just this competitor.
        if (competitor.added && !isCurrentTeamNationalTeam && !isCricket) {
          const { data: contracts } = await axios.get(
            `${url.persons}/${competitor.id}${url.contracts}?filter=personRole[in]:PlaysFor~isActive[equals]:true`,
          );
          const hasExistingContracts = contracts?.items?.length > 0;
          if (hasExistingContracts) {
            const isOtherTeamNationalTeam = await getOldContractTeamMetadata(
              contracts.items[0]?.sport?.id,
            );
            if (!isOtherTeamNationalTeam) {
              const confirmed = await askContractConfirmation(competitor);
              if (!confirmed) {
                continue;
              }
            }
          }
        }

        if (competitor.added) {
          const payload = {
            metadataProperties: populateMetadata(competitor, contractsMetadata),
            personRole: 1,
            personId: competitor.id,
            isActive: competitor.isActive,
            startDate: competitor.startDate,
            endDate: competitor.endDate,
            competitorId: id,
          };
          const result = await axios.post('/contracts', payload);
          if (result.status === 201) {
            savedById.set(competitor.id, { contractId: result.data.id });
          }
        } else if (competitor.edited) {
          const payload = {
            id: competitor.contractId,
            isActive: competitor.isActive,
            startDate: competitor.startDate,
            endDate: competitor.endDate,
            metadataProperties: populateMetadata(competitor, contractsMetadata),
          };
          const result = await axios.put('/contracts', payload);
          if (result.status === 200) {
            savedById.set(competitor.id, {});
          }
        }
      } catch {
        // Server error - keep the competitor flagged as edited/added so the
        // user can retry. Successful peers in this batch are still committed.
      }
    }

    if (savedById.size === 0) {
      return;
    }

    const updatedCompetitors = competitors.map((item: EditableCompetitor) => {
      const saved = savedById.get(item.id);
      if (!saved) {
        return item;
      }
      return {
        ...item,
        ...(saved.contractId !== undefined ? { contractId: saved.contractId } : {}),
        edited: false,
        added: false,
      };
    });

    setValue('competitors', updatedCompetitors, { shouldDirty: false });
    const oldValues = getValues();
    resetForm({
      ...oldValues,
      competitors: updatedCompetitors,
    });
    await refetchCompetitors();
  }, [
    competitors,
    contractsMetadata,
    id,
    sportId,
    sports,
    teamMetadata,
    setValue,
    getValues,
    resetForm,
    refetchCompetitors,
    askContractConfirmation,
  ]);

  const hasModifiedCompetitors = competitors.some(
    (competitor: EditableCompetitor) => competitor.edited || competitor.added,
  );

  const contractConfirmationMessage = pendingCompetitor
    ? `${strings.contract_exists} (${pendingCompetitor.name})`
    : strings.contract_exists;

  useEffect(() => {
    rows.forEach(competitor => {
      if (competitor.deleted) {
        handleBackgroundColorChange(competitor.id, stateColors.deleted);
      } else if (competitor.edited) {
        handleBackgroundColorChange(competitor.id, stateColors.edited);
      } else if (competitor.added) {
        handleBackgroundColorChange(competitor.id, stateColors.added);
      } else {
        handleBackgroundColorChange(competitor.id, '');
      }
    });
  }, [handleBackgroundColorChange, rows, stateColors]);
  return (
    <>
      <PromptDialog
        open={isContractConfirmationOpen}
        confirmMessage={contractConfirmationMessage}
        buttonText={strings.confirm}
        confirmCallback={async (result: boolean) => await handleContractConfirmation(result)}
      />
      <BaseCompetitorsFormField
        sportId={sportId}
        customColumns={customColumns}
        customFilters={customFilters}
        onEditCell={handleEdit}
        onAdd={handleAdd}
        competitors={competitors}
        rows={rows}
        idsRef={idsRef}
        isActiveContracts={isActiveContracts}
        setActiveContracts={setActiveContracts}
        setFilterBy={setFilterBy}
        setSortBy={setSortBy}
        genderType={genderType}
        onSaveAll={handleSaveAll}
        hasModifiedCompetitors={hasModifiedCompetitors}
      />
    </>
  );
}
